import setWith from 'lodash/setWith';
import unset from 'lodash/unset';

/**
 * @typedef {Object<string, any>} I18n
 * @property {function(string, any=)} t
 * @property {string} locale
 * @property {string} defaultLocale
 */

/**
 * Generate translation path for the given locale and component id
 * @param {string | number} uid
 * @param {string} namespace
 * @param {string=} localeKey
 * @return {string}
 */
const generatePathForNamespace = (namespace) => (uid, localeKey) => [
  ...(localeKey ? [localeKey] : []),
  namespace,
  uid,
].join('.');

/**
 *
 * @param {I18n} i18nInstance
 * @param {function(string): string} componentScopeGetter
 */
const createInComponentTranslationInstance = (i18nInstance, componentScopeGetter) =>
  /**
   * @function
   * "Proxy" i18n-js translation to includes component's translation
   * @param {string} scope
   * @param {any={}} option
   */
  (scope, option = {}) => {
    const componentScope = componentScopeGetter(scope);
    const translationOption = {
      ...option,
      defaults: [
        ...(option.defaults || []),
        { scope },
      ],
    };
    return i18nInstance.t(componentScope, translationOption);
  }
;

export class TranslationSupport {
  /**
   * @param {I18n} i18nInstance
   * @param {{
   *   namespace?: string
   * }} config
   */
  constructor(i18nInstance, config = {}) {
    this.config = {
      namespace: config.namespace == null ? '__viddsee_vue__' : config.namespace,
    };

    this.generateNamespacedPath = generatePathForNamespace(this.config.namespace);
    this.i18nInstance = i18nInstance;
  }

  /**
   * @param {Record<string, any>} message
   * @param {string | number} uid
   */
  addTranslations(message, uid) {
    if (!message) {
      return;
    }

    Object.entries(message)
      .forEach(([localeKey, dictionary]) => {
        setWith(
          this.i18nInstance.translations,
          this.generateNamespacedPath(uid, localeKey),
          dictionary,
          Object,
        );
      })
    ;
  }

  /**
   * @param {Record<string, any>} message
   * @param {string | number} uid
   */
  removeTranslations(messages, uid) {
    if (!messages) {
      return;
    }

    Object.keys(messages)
      .forEach((localeKey) => {
        unset(
          this.i18nInstance.translations,
          this.generateNamespacedPath(uid, localeKey),
        );
      })
    ;
  }

  provideInstance(uid, messages) {
    if (!messages) {
      return this.i18nInstance.t.bind(this.i18nInstance);
    }

    /**
     * @param {string} scope
     */
    const componentScopeGetter = scope => `${this.generateNamespacedPath(uid)}.${scope}`;
    return createInComponentTranslationInstance(this.i18nInstance, componentScopeGetter);
  }

  setup(target = '$t') {
    const translationSupport = this;

    // Supply Vue setup option
    return {
      beforeCreate() {
        // Skip operation if an existing translation service is present
        if (target in this) {
          return;
        }

        const messages = this.$options && this.$options.i18n;
        translationSupport.addTranslations(messages, this._uid);

        const translationInstance = translationSupport.provideInstance(this._uid, messages);
        this[target] = translationInstance;
      },
      beforeDestroy() {
        const messages = this.$options && this.$options.i18n;
        translationSupport.removeTranslations(messages, this._uid);
      },
    };
  }
}
